/**
 * FuelGaugeBar.js
 *
 * Horizontal progress bar showing fuel level.
 * Shows departure fuel vs current/arrival fuel for comparison.
 * Used in VoyageHeader during underway phase.
 */

import React from 'react';
import { Fuel } from 'lucide-react';

const FuelGaugeBar = ({
  departureFuel,
  currentFuel,
  compact = false
}) => {
  // Don't render if no departure fuel set
  if (departureFuel === undefined || departureFuel === null) {
    return null;
  }

  const displayFuel = currentFuel !== undefined && currentFuel !== null
    ? currentFuel
    : departureFuel;

  // Calculate consumption (only if we have current fuel)
  const consumption = currentFuel !== undefined && currentFuel !== null
    ? departureFuel - currentFuel
    : null;

  // Color based on current fuel level
  const getBarColor = (level) => {
    if (level >= 75) return 'bg-green-500';
    if (level >= 50) return 'bg-cyan-500';
    if (level >= 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Warning state for low fuel
  const isLowFuel = displayFuel <= 25;

  if (compact) {
    // Compact version for header
    return (
      <div className="flex items-center gap-2">
        <Fuel className={`h-4 w-4 ${isLowFuel ? 'text-red-500' : 'text-slate-400'}`} />
        <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${getBarColor(displayFuel)}`}
            style={{ width: `${displayFuel}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${isLowFuel ? 'text-red-500' : 'text-slate-500'}`}>
          {displayFuel}%
        </span>
      </div>
    );
  }

  // Full version with labels
  return (
    <div className={`p-3 rounded-lg ${isLowFuel ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Fuel className={`h-4 w-4 ${isLowFuel ? 'text-red-500' : 'text-slate-500'}`} />
          <span className="text-sm font-medium text-slate-700">Fuel Level</span>
        </div>
        <span className={`text-sm font-semibold ${isLowFuel ? 'text-red-600' : 'text-slate-700'}`}>
          {displayFuel}%
        </span>
      </div>

      {/* Gauge bar */}
      <div className="relative h-3 bg-slate-200 rounded-full overflow-hidden mb-2">
        {/* Departure fuel marker (shown as background if consumption occurred) */}
        {consumption !== null && consumption > 0 && (
          <div
            className="absolute h-full bg-slate-300"
            style={{ width: `${departureFuel}%` }}
          />
        )}
        {/* Current fuel level */}
        <div
          className={`relative h-full transition-all duration-500 ${getBarColor(displayFuel)}`}
          style={{ width: `${displayFuel}%` }}
        />
      </div>

      {/* Stats row */}
      <div className="flex justify-between text-xs text-slate-500">
        <span>Departure: {departureFuel}%</span>
        {consumption !== null && consumption > 0 && (
          <span className="text-amber-600">Used: {consumption}%</span>
        )}
      </div>

      {/* Low fuel warning */}
      {isLowFuel && (
        <div className="mt-2 text-xs text-red-600 font-medium">
          Low fuel - consider refueling
        </div>
      )}
    </div>
  );
};

export default FuelGaugeBar;
